<template>
  <div class="user-list">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-user-solid"></i>
              用户管理
            </h1>
            <p class="page-subtitle">管理系统用户，包括会员等级、门店绑定等功能</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 统计卡片 -->
      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-icon total-users">
            <i class="el-icon-user-solid"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.total_users || 0 }}</div>
            <div class="stat-label">总用户数</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon active-users">
            <i class="el-icon-circle-check"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.active_users || 0 }}</div>
            <div class="stat-label">活跃用户</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon inactive-users">
            <i class="el-icon-warning-outline"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.inactive_users || 0 }}</div>
            <div class="stat-label">禁用用户</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon new-users">
            <i class="el-icon-plus"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ statistics.new_today || 0 }}</div>
            <div class="stat-label">今日新增</div>
          </div>
        </div>
      </div>

      <!-- 筛选区域 -->
      <div class="filter-section">
        <div class="filter-card">
          <el-form :model="searchForm" :inline="true" class="filter-form">
            <div class="form-row">
              <div class="form-item">
                <label class="form-label">搜索用户</label>
                <el-input
                  v-model="searchForm.keyword"
                  placeholder="请输入昵称或手机号"
                  clearable
                  size="large"
                  prefix-icon="el-icon-search"
                />
              </div>
              
              <div class="form-item">
                <label class="form-label">会员等级</label>
                <el-select
                  v-model="searchForm.membership_level_id"
                  placeholder="全部等级"
                  clearable
                  size="large"
                >
                  <el-option
                    v-for="level in membershipLevels"
                    :key="level.id"
                    :label="level.levelName || level.level_name"
                    :value="level.id"
                  />
                </el-select>
              </div>
              
              <div class="form-item">
                <label class="form-label">用户状态</label>
                <el-select
                  v-model="searchForm.is_active"
                  placeholder="全部状态"
                  clearable
                  size="large"
                >
                  <el-option label="启用" :value="true" />
                  <el-option label="禁用" :value="false" />
                </el-select>
              </div>
              
              <div class="form-item">
                <label class="form-label">所属门店</label>
                <el-input
                  v-model="searchForm.store_id"
                  placeholder="请输入门店ID"
                  clearable
                  size="large"
                />
              </div>
            </div>
            
            <div class="form-actions">
              <el-button type="primary" @click="handleSearch" size="large">
                <i class="el-icon-search"></i> 搜索
              </el-button>
              <el-button @click="handleReset" size="large">
                <i class="el-icon-refresh-left"></i> 重置
              </el-button>
            </div>
          </el-form>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="table-section">
        <div class="table-card">
          <div class="table-header">
            <h3>
              <i class="el-icon-menu"></i>
              用户列表
            </h3>
            <p>共 {{ total }} 条记录</p>
          </div>

          <el-table
            v-loading="loading"
            :data="userList"
            style="width: 100%"
            class="user-table"
          >
            <el-table-column prop="id" label="ID" width="80" align="center">
              <template slot-scope="scope">
                <span class="user-id">#{{ scope.row.id }}</span>
              </template>
            </el-table-column>
            
            <el-table-column label="用户信息" min-width="200">
              <template slot-scope="scope">
                <div class="user-info-cell">
                  <div class="user-avatar">
                    <el-avatar :size="40" :src="scope.row.avatar" icon="el-icon-user-solid" />
                  </div>
                  <div class="user-details">
                    <div class="nickname">{{ scope.row.nickname || '未设置昵称' }}</div>
                    <div class="user-meta">
                      <span v-if="scope.row.phone" class="phone">
                        <i class="el-icon-mobile-phone"></i>
                        {{ scope.row.phone }}
                      </span>
                      <span v-if="scope.row.gender" class="gender">
                        <el-tag :type="scope.row.gender === 1 ? 'info' : 'warning'" size="mini">
                          {{ scope.row.gender === 1 ? '男' : '女' }}
                        </el-tag>
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column label="会员等级" width="120" align="center">
              <template slot-scope="scope">
                <el-tag
                  v-if="scope.row.membership_level"
                  :class="getMembershipClass(scope.row.membership_level.id)"
                  class="membership-tag"
                >
                  <i class="el-icon-medal"></i>
                  {{ scope.row.membership_level.level_name }}
                </el-tag>
                <span v-else class="no-level">-</span>
              </template>
            </el-table-column>
            
            <el-table-column label="所属门店" min-width="150">
              <template slot-scope="scope">
                <div class="store-info-cell" v-if="scope.row.bind_store">
                  <i class="el-icon-office-building"></i>
                  <div class="store-details">
                    <div class="store-name">{{ scope.row.bind_store.store_name }}</div>
                    <div class="store-id">ID: {{ scope.row.bind_store.id }}</div>
                  </div>
                </div>
                <span v-else class="no-store">未绑定</span>
              </template>
            </el-table-column>
            
            <el-table-column label="消费统计" width="130" align="center">
              <template slot-scope="scope">
                <div class="consumption-info">
                  <div class="amount">¥{{ scope.row.total_consumption || 0 }}</div>
                  <div class="quantity">{{ scope.row.total_quantity || 0 }}件</div>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column label="状态" width="80" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.is_active ? 'success' : 'danger'" size="small">
                  {{ scope.row.is_active ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            
            <el-table-column label="注册时间" width="160">
              <template slot-scope="scope">
                <div class="time-info">
                  <i class="el-icon-time"></i>
                  {{ scope.row.created_at | formatDate }}
                </div>
              </template>
            </el-table-column>
            
            <el-table-column label="操作" width="280" fixed="right" align="center">
              <template slot-scope="scope">
                <div class="action-buttons">
                  <el-button
                    size="mini"
                    icon="el-icon-view"
                    @click="handleViewDetail(scope.row)"
                    title="查看详情"
                  >
                    详情
                  </el-button>
                  
                  <el-button
                    size="mini"
                    type="primary"
                    icon="el-icon-medal"
                    @click="handleEditLevel(scope.row)"
                    title="修改会员等级"
                  >
                    等级
                  </el-button>
                  
                  <el-button
                    v-if="!scope.row.is_active"
                    size="mini"
                    type="success"
                    icon="el-icon-check"
                    @click="handleToggleStatus(scope.row, true)"
                    title="启用用户"
                  >
                    启用
                  </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
          
          <!-- 分页 -->
          <div class="pagination-wrapper">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pagination.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.page_size"
              layout="total, sizes, prev, pager, next, jumper"
              :total="total"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 用户详情对话框 -->
    <user-detail-dialog
      :visible.sync="detailDialogVisible"
      :user-id="currentUserId"
      @refresh="loadUserList"
      @edit-level="handleEditLevelFromDetail"
    />

    <!-- 会员等级修改对话框 -->
    <membership-level-dialog
      :visible.sync="levelDialogVisible"
      :user-id="currentUserId"
      @success="handleLevelChangeSuccess"
    />
  </div>
</template>

<script>
import { getUserList, updateUserStatus } from '@/api/user'
import { getMembershipLevelList } from '@/api/membershipLevel'
import { mapGetters } from 'vuex'
import UserDetailDialog from './components/UserDetailDialog.vue'
import MembershipLevelDialog from './components/MembershipLevelDialog.vue'

export default {
  name: 'UserList',
  
  components: {
    UserDetailDialog,
    MembershipLevelDialog
  },
  
  filters: {
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },
  
  data() {
    return {
      loading: false,
      userList: [],
      total: 0,
      
      // 统计数据
      statistics: {
        total_users: 0,
        active_users: 0,
        inactive_users: 0,
        new_today: 0
      },
      
      // 搜索表单
      searchForm: {
        keyword: '',
        membership_level_id: null,
        is_active: null,
        store_id: ''
      },
      
      // 分页
      pagination: {
        page: 1,
        page_size: 20
      },
      
      // 会员等级列表
      membershipLevels: [],
      
      // 对话框控制
      detailDialogVisible: false,
      levelDialogVisible: false,
      currentUserId: null
    }
  },
  
  mounted() {
    this.loadUserList()
    this.loadMembershipLevels()
  },
  
  computed: {
    ...mapGetters(['adminInfo']),
    
    // 判断当前用户是否为超级管理员
    isSuperAdmin() {
      return this.adminInfo && this.adminInfo.role_type === 1
    }
  },
  
  methods: {
    // 加载会员等级列表（仅启用状态）
    async loadMembershipLevels() {
      try {
        const res = await getMembershipLevelList({ 
          is_active: true,  // 只获取启用状态的会员等级
          page_size: 100    // 获取所有启用的等级
        })
        this.membershipLevels = res.data?.list || []
        console.log('UserList 加载会员等级列表:', this.membershipLevels)
      } catch (error) {
        console.error('获取会员等级列表失败:', error)
        this.$message.error('获取会员等级列表失败')
      }
    },
    
    // 加载用户列表
    async loadUserList() {
      this.loading = true
      try {
        const params = {
          ...this.pagination,
          ...this.searchForm
        }
        
        // 移除空值
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })
        
        const res = await getUserList(params)
        
        this.userList = res.data.list || []
        this.total = res.data.pagination?.total || 0
        
        // 更新统计数据
        if (res.data.statistics) {
          this.statistics = res.data.statistics
        }
        
        // 获取今日新增用户数统计
        this.loadTodayNewUsersCount()
        
      } catch (error) {
        console.error('加载用户列表失败:', error)
        this.$message.error(error.message || '加载用户列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 获取今日新增用户数统计
    async loadTodayNewUsersCount() {
      try {
        // 使用和仪表板相同的API获取统计数据
        const { getDashboard } = await import('@/api/admin')
        const res = await getDashboard()
        
        if (res && res.data && res.data.recentStats) {
          const newTodayValue = res.data.recentStats.newUsersToday || 0
          // 使用Vue.set确保响应式更新
          this.$set(this.statistics, 'new_today', newTodayValue)
        }
        
      } catch (error) {
        console.error('获取今日新增用户数失败:', error)
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.page = 1
      this.loadUserList()
    },
    
    // 重置
    handleReset() {
      this.searchForm = {
        keyword: '',
        membership_level_id: null,
        is_active: null,
        store_id: ''
      }
      this.pagination.page = 1
      this.loadUserList()
    },
    
    // 查看详情
    handleViewDetail(row) {
      this.currentUserId = row.id
      this.detailDialogVisible = true
    },
    
    // 切换用户状态
    async handleToggleStatus(row, status) {
      const action = status ? '启用' : '禁用'
      
      try {
        await this.$confirm(
          `确定要${action}用户"${row.nickname || row.phone}"吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        await updateUserStatus(row.id, {
          is_active: status,
          reason: `管理员${action}操作`
        })
        
        this.$message.success(`用户${action}成功`)
        this.loadUserList()
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('状态切换失败:', error)
          this.$message.error(error.message || `用户${action}失败`)
        }
      }
    },
    
    
    // 修改等级
    handleEditLevel(row) {
      this.currentUserId = row.id
      this.levelDialogVisible = true
    },
    
    // 从详情页触发修改等级
    handleEditLevelFromDetail(userId) {
      this.currentUserId = userId
      this.levelDialogVisible = true
    },
    
    // 等级修改成功
    handleLevelChangeSuccess() {
      this.loadUserList()
      // 如果详情对话框打开，也需要刷新详情
      if (this.detailDialogVisible) {
        // 详情对话框会监听 visible 变化自动刷新
      }
    },
    
    
    
    // 分页：每页数量变化
    handleSizeChange(val) {
      this.pagination.page_size = val
      this.pagination.page = 1
      this.loadUserList()
    },
    
    // 分页：页码变化
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadUserList()
    },
    
    // 获取会员等级颜色（使用API中的通用函数）
    getMembershipColor(levelId, sortOrder = null) {
      // 导入API中的函数
      const { getMembershipLevelColor } = require('@/api/user')
      return getMembershipLevelColor(levelId, sortOrder)
    },
    
    // 获取会员等级样式类（基于排序序号的动态样式）
    getMembershipClass(levelId, sortOrder = null) {
      if (sortOrder !== null) {
        const classNames = [
          'level-normal',    // 普通等级
          'level-bronze',    // 青铜等级
          'level-silver',    // 白银等级
          'level-gold',      // 黄金等级
          'level-platinum',  // 铂金等级
          'level-diamond',   // 钻石等级
          'level-supreme',   // 至尊等级
          'level-legend'     // 传奇等级
        ]
        return classNames[sortOrder - 1] || classNames[classNames.length - 1]
      }
      
      // 兼容原有的ID映射（向后兼容）
      const classMap = {
        1: 'level-normal',
        2: 'level-bronze',
        3: 'level-silver',
        4: 'level-gold',
        5: 'level-platinum'
      }
      return classMap[levelId] || ''
    }
  }
}
</script>

<style scoped>
/* 主容器 */
.user-list {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ==================== 页面头部 ==================== */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  flex: 1;
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: white;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.header-actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.header-actions .el-button--primary {
  background: rgba(255, 255, 255, 0.95);
  color: #667eea;
  border-color: transparent;
}

.header-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.header-actions .el-button--primary:hover {
  background: white;
  color: #667eea;
}

/* ==================== 主内容区域 ==================== */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
}

/* ==================== 统计卡片 ==================== */
.stats-section {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f3f4;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  position: relative;
  flex-shrink: 0;
}

.stat-icon::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 22px;
  padding: 2px;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
}

.stat-icon.total-users::before {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.stat-icon.total-users i {
  color: #667eea;
}

.stat-icon.active-users::before {
  background: linear-gradient(135deg, #f093fb, #f5576c);
}

.stat-icon.active-users i {
  color: #f093fb;
}

.stat-icon.inactive-users::before {
  background: linear-gradient(135deg, #ffecd2, #fcb69f);
}

.stat-icon.inactive-users i {
  color: #e6a23c;
}

.stat-icon.new-users::before {
  background: linear-gradient(135deg, #4facfe, #00f2fe);
}

.stat-icon.new-users i {
  color: #4facfe;
}

.stat-icon i {
  z-index: 1;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* ==================== 筛选区域 ==================== */
.filter-section {
  margin-bottom: 30px;
}

.filter-card {
  background: white;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
}

.filter-form {
  margin: 0;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.form-item {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
}

.form-actions {
  text-align: center;
}

.form-actions .el-button {
  min-width: 120px;
  margin: 0 10px;
}

/* ==================== 表格区域 ==================== */
.table-section {
  margin-bottom: 30px;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  overflow: hidden;
}

.table-header {
  padding: 25px 30px 15px;
  border-bottom: 1px solid #f0f2f5;
  background: linear-gradient(135deg, #fafbfc, #f8f9fa);
}

.table-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 5px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-header h3 i {
  color: #667eea;
  font-size: 20px;
}

.table-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
}



/* ==================== 表格样式 ==================== */
.user-table :deep(.el-table) {
  border: none;
}

.user-table :deep(.el-table th) {
  background: #fafbfc;
  border: none;
  color: #606266;
  font-weight: 600;
}

.user-table :deep(.el-table td) {
  border: none;
  padding: 15px 10px;
}

.user-table :deep(.el-table--border::after) {
  display: none;
}

.user-table :deep(.el-table::before) {
  display: none;
}

.user-table {
  font-size: 14px;
}

.user-table :deep(.el-table__header-wrapper) {
  background: #f8f9fa;
}

.user-table :deep(.el-table th) {
  background: #f8f9fa;
  color: #606266;
  font-weight: 600;
  font-size: 13px;
}

.user-table :deep(.el-table td),
.user-table :deep(.el-table th) {
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;
}

.user-table :deep(.el-table__row:hover) {
  background: #f5f7fa;
}

/* 用户ID */
.user-id {
  font-weight: 600;
  color: #409eff;
  font-family: 'Courier New', monospace;
}

/* 用户信息单元格 */
.user-info-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  flex-shrink: 0;
}

.user-details {
  flex: 1;
  min-width: 0;
}

.nickname {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.user-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #909399;
}

.user-meta .phone {
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-meta .phone i {
  font-size: 13px;
}

/* 会员等级标签 */
.membership-tag {
  border: none;
  font-weight: 600;
  padding: 6px 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.membership-tag.level-normal {
  background: #909399;
  color: white;
}

.membership-tag.level-bronze {
  background: linear-gradient(135deg, #cd7f32 0%, #b87333 100%);
  color: white;
}

.membership-tag.level-silver {
  background: linear-gradient(135deg, #c0c0c0 0%, #a8a8a8 100%);
  color: white;
}

.membership-tag.level-gold {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #333;
}

.membership-tag.level-platinum {
  background: linear-gradient(135deg, #e5e4e2 0%, #c0c0c0 100%);
  color: #333;
}

.no-level {
  color: #c0c4cc;
  font-size: 13px;
}

/* 门店信息单元格 */
.store-info-cell {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #606266;
}

.store-info-cell i {
  color: #409eff;
  font-size: 16px;
  margin-top: 2px;
  flex-shrink: 0;
}

.store-details {
  flex: 1;
}

.store-name {
  font-weight: 600;
  color: #303133;
  margin-bottom: 2px;
}

.store-id {
  font-size: 12px;
  color: #909399;
}

.no-store {
  color: #c0c4cc;
  font-size: 13px;
}

/* 消费统计 */
.consumption-info {
  text-align: center;
}

.consumption-info .amount {
  font-size: 16px;
  font-weight: 700;
  color: #f56c6c;
  margin-bottom: 4px;
}

.consumption-info .quantity {
  font-size: 13px;
  color: #909399;
}

/* 时间信息 */
.time-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #606266;
}

.time-info i {
  color: #909399;
  font-size: 14px;
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 8px 4px;
}

.action-buttons .el-button {
  margin: 0;
  min-width: 50px;
  font-size: 12px;
  padding: 7px 12px;
}

.action-buttons .el-button--danger {
  background: #f56c6c;
  border-color: #f56c6c;
  color: white;
}

.action-buttons .el-button--danger:hover {
  background: #f78989;
  border-color: #f78989;
}

.action-buttons .el-button--warning {
  background: #e6a23c;
  border-color: #e6a23c;
  color: white;
}

.action-buttons .el-button--warning:hover {
  background: #ebb563;
  border-color: #ebb563;
}

.action-buttons .el-button--success {
  background: #67c23a;
  border-color: #67c23a;
  color: white;
}

.action-buttons .el-button--success:hover {
  background: #85ce61;
  border-color: #85ce61;
}

.action-buttons .el-button--primary {
  background: #409eff;
  border-color: #409eff;
  color: white;
}

.action-buttons .el-button--primary:hover {
  background: #66b1ff;
  border-color: #66b1ff;
}

/* ==================== 分页 ==================== */
.pagination-wrapper {
  padding: 20px 30px;
  text-align: right;
  background: #fafbfc;
  border-top: 1px solid #f0f2f5;
}

/* ==================== 响应式 ==================== */
@media (max-width: 1200px) {
  .stats-section {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .form-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 16px 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .header-actions {
    width: 100%;
    flex-wrap: wrap;
  }
  
  .header-actions .el-button {
    flex: 1;
  }
  
  .main-content {
    padding: 16px 20px;
  }
  
  .stats-section {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .stat-card {
    padding: 16px;
  }
  
  .stat-value {
    font-size: 24px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .form-actions .el-button {
    width: 100%;
  }
  
  .table-card {
    padding: 16px;
    overflow-x: auto;
  }
  
  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>

